/**
 * Configuración → Empleado (Fase A · Fundamentos).
 *
 * Sub-tabs:
 *   - Empleados         Lista con saldo por año + excepción individual.
 *   - Calendario laboral  Festivos y días no laborables.
 *   - Vacaciones        (Fase B — placeholder).
 *   - Nóminas           (Fase C — placeholder).
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users, CalendarDays, PlaneTakeoff, FileText, Plus, Trash2, Save, Pencil, X } from "lucide-react";

const SUB_TABS = [
  { id: "employees", label: "Empleados",         icon: Users },
  { id: "calendar",  label: "Calendario laboral", icon: CalendarDays },
  { id: "vacations", label: "Vacaciones",         icon: PlaneTakeoff },
  { id: "payroll",   label: "Nóminas",            icon: FileText },
];

export default function EmployeeTab() {
  const [sub, setSub] = useState("employees");
  return (
    <div data-testid="settings-employee-tab">
      <div className="flex gap-1 border-b border-[#E6E7E1] mb-4 overflow-x-auto no-scrollbar">
        {SUB_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setSub(t.id)}
            data-testid={`emp-sub-${t.id}`}
            className={`px-3 py-2 text-sm font-semibold inline-flex items-center gap-1.5 border-b-2 whitespace-nowrap transition-colors ${
              sub === t.id
                ? "text-[#0F1210] border-[#6E8B3D]"
                : "text-[#8A9098] border-transparent hover:text-[#4B5158]"
            }`}
          >
            <t.icon size={13} /> {t.label}
            {t.phase && <span className="ml-1 text-[9px] font-mono px-1.5 py-[1px] rounded-full bg-[#FFF7E6] text-[#8C5A00]">Fase {t.phase}</span>}
          </button>
        ))}
      </div>
      {sub === "employees" && <EmployeesPanel />}
      {sub === "calendar"  && <HolidaysPanel />}
      {sub === "vacations" && <VacationsAdminPanel />}
      {sub === "payroll"   && <PayrollsAdminPanel />}
    </div>
  );
}

/* ============================================================ */
/* Nóminas · Admin (Fase C)                                     */
/* ============================================================ */

function PayrollsAdminPanel() {
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [filterUser, setFilterUser] = useState("");
  const now = new Date();
  const [form, setForm] = useState({
    user_id: "",
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    file: null,
  });
  const [uploading, setUploading] = useState(false);

  const loadList = () => {
    const params = filterUser ? { user_id: filterUser } : {};
    api.get("/settings/payrolls", { params }).then((r) => setRows(r.data));
  };
  useEffect(() => {
    api.get("/settings/employees").then((r) => setEmployees(r.data));
  }, []);
  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [filterUser]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.user_id || !form.file) return toast.error("Empleado y PDF obligatorios");
    if (!form.file.name.toLowerCase().endsWith(".pdf")) return toast.error("El archivo debe ser PDF");
    const fd = new FormData();
    fd.append("user_id", form.user_id);
    fd.append("year", String(form.year));
    fd.append("month", String(form.month));
    fd.append("file", form.file);
    setUploading(true);
    try {
      await api.post("/settings/payrolls", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Nómina subida");
      setForm({ ...form, file: null });
      document.getElementById("payroll-file-input").value = "";
      loadList();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`¿Eliminar nómina de ${r.user_name} ${r.period}?`)) return;
    try { await api.delete(`/settings/payrolls/${r.id}`); toast.success("Eliminada"); loadList(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const download = async (r) => {
    try {
      const resp = await api.get(`/settings/payrolls/${r.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = r.original_filename || `nomina_${r.period}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-payrolls">
      <form onSubmit={submit} className="ds-card p-5">
        <div className="font-display font-bold text-sm mb-3">Subir nómina</div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_120px_140px_1fr_auto] gap-3 items-end">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">Empleado</label>
            <select className="ds-select mt-1" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} required data-testid="payroll-user">
              <option value="">— Selecciona empleado —</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">Mes</label>
            <select className="ds-select mt-1" value={form.month} onChange={(e) => setForm({ ...form, month: Number(e.target.value) })} data-testid="payroll-month">
              {Array.from({length:12},(_,i)=>i+1).map((m) => (
                <option key={m} value={m}>{["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"][m-1]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">Año</label>
            <input type="number" min={2000} max={2100} className="ds-input mt-1" value={form.year}
                   onChange={(e) => setForm({ ...form, year: Number(e.target.value) })} data-testid="payroll-year"/>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">PDF</label>
            <input id="payroll-file-input" type="file" accept="application/pdf" className="ds-input mt-1"
                   onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} data-testid="payroll-file" required/>
          </div>
          <button type="submit" className="ds-btn primary" disabled={uploading} data-testid="payroll-submit">
            <FileText size={13}/> {uploading ? "Subiendo…" : "Subir"}
          </button>
        </div>
        <div className="mt-2 text-[10.5px] text-[#8A9098] flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-[#8A9098]"/>
          Al subir una nueva nómina, la 4ª más antigua se elimina automáticamente.
        </div>
      </form>

      <div className="ds-card">
        <div className="ds-card-h">
          <div className="t">Nóminas cargadas</div>
          <select className="ds-select" style={{ padding: "5px 10px", fontSize: 12, width: 220 }}
                  value={filterUser} onChange={(e) => setFilterUser(e.target.value)} data-testid="payroll-filter-user">
            <option value="">Todos los empleados</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left font-mono text-[10.5px] uppercase tracking-[.14em] text-[#8A9098] bg-[#FBFBF8] border-b border-[#E6E7E1]">
              <th className="px-4 py-2.5">Empleado</th>
              <th className="px-4 py-2.5">Periodo</th>
              <th className="px-4 py-2.5">Archivo</th>
              <th className="px-4 py-2.5">Subida</th>
              <th className="px-4 py-2.5 w-40 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-[#E6E7E1] last:border-0" data-testid={`payroll-row-${r.id}`}>
                <td className="px-4 py-2.5 font-semibold">{r.user_name}</td>
                <td className="px-4 py-2.5 font-mono">{r.period}</td>
                <td className="px-4 py-2.5 text-[#4B5158] text-xs">{r.original_filename} · {(r.size/1024).toFixed(1)} KB</td>
                <td className="px-4 py-2.5 text-xs text-[#8A9098]">{new Date(r.uploaded_at).toLocaleDateString("es-ES")} · {r.uploaded_by}</td>
                <td className="px-4 py-2.5 text-right">
                  <div className="inline-flex gap-1">
                    <button className="ds-btn" style={{ padding: "5px 10px", fontSize: 11 }} onClick={() => download(r)} data-testid={`payroll-download-${r.id}`}>
                      <FileText size={11}/> Ver
                    </button>
                    <button className="ds-btn danger" style={{ padding: "5px 10px", fontSize: 11 }} onClick={() => remove(r)} data-testid={`payroll-delete-${r.id}`}>
                      <Trash2 size={11}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-[#8A9098]">Sin nóminas cargadas</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============================================================ */
/* Vacaciones · Admin (Fase B)                                  */
/* ============================================================ */

function VacationsAdminPanel() {
  const [filter, setFilter] = useState("pending");
  const [rows, setRows] = useState([]);
  const [rejecting, setRejecting] = useState(null);  // { id, comment }
  const load = () => api.get("/settings/vacation-requests", { params: { status: filter } }).then((r) => setRows(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const approve = async (id) => {
    if (!window.confirm("¿Aprobar esta solicitud?")) return;
    try { await api.post(`/settings/vacation-requests/${id}/approve`); toast.success("Aprobada"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const doReject = async () => {
    if (!rejecting) return;
    try {
      await api.post(`/settings/vacation-requests/${rejecting.id}/reject`, { comment: rejecting.comment });
      toast.success("Rechazada"); setRejecting(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-vacations">
      <div className="flex gap-1 flex-wrap">
        {[
          ["pending", "Pendientes"],
          ["approved", "Aprobadas"],
          ["rejected", "Rechazadas"],
          ["all", "Todas"],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)}
                  data-testid={`vac-filter-${k}`}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                    filter === k ? "bg-[#0F1210] text-white" : "bg-[#F5F6F2] text-[#4B5158] hover:bg-[#EEF0EA]"
                  }`}>{l}</button>
        ))}
      </div>

      {rows.length === 0 && (
        <div className="ds-card p-10 text-center text-[#8A9098] text-sm">Sin solicitudes en este filtro</div>
      )}
      {rows.map((r) => {
        const groups = groupConsecAdmin(r.billable_days || []);
        return (
          <div key={r.id} className="ds-card p-5" data-testid={`admin-req-${r.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <div className="font-display font-black text-base tracking-tight">{r.user_name}</div>
                  <span className="text-[11px] text-[#8A9098] font-mono">{r.user_email}</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="font-display font-black text-2xl tracking-tight text-[#3F5222]">{r.billable_count}</span>
                  <span className="text-sm text-[#4B5158]">días solicitados</span>
                  <span className="text-xs text-[#8A9098]">· {formatDate(r.created_at)}</span>
                </div>
                {r.comment && <div className="mt-2 text-[12.5px] text-[#4B5158] italic">"{r.comment}"</div>}
                <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-[12px] font-mono">
                  {groups.map((g, i) => (
                    <li key={i} className="px-2 py-1 rounded-md bg-[#FBFBF8] border border-[#E6E7E1] flex justify-between">
                      <span>{g.start === g.end ? fmtAdmin(g.start) : `${fmtAdmin(g.start)} → ${fmtAdmin(g.end)}`}</span>
                      <b className="text-[#3F5222]">{g.count}d</b>
                    </li>
                  ))}
                </ul>
                {r.status !== "pending" && (
                  <div className="mt-3 text-[11px] text-[#8A9098]">
                    {r.status === "approved" ? "Aprobada" : "Rechazada"} por {r.decided_by} · {formatDate(r.decided_at)}
                    {r.decision_comment && <span className="ml-2 text-[#B4381F]">· "{r.decision_comment}"</span>}
                  </div>
                )}
              </div>
              {r.status === "pending" && (
                <div className="flex gap-2">
                  <button className="ds-btn primary" onClick={() => approve(r.id)} data-testid={`admin-approve-${r.id}`}>
                    Aprobar
                  </button>
                  <button className="ds-btn danger" onClick={() => setRejecting({ id: r.id, comment: "" })} data-testid={`admin-reject-${r.id}`}>
                    Rechazar
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {rejecting && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setRejecting(null)}>
          <div className="ds-card w-full max-w-md" style={{ boxShadow: "var(--shadow-lg)" }} onClick={(e) => e.stopPropagation()}>
            <div className="ds-card-h">
              <div className="t">Rechazar solicitud</div>
              <button onClick={() => setRejecting(null)} className="p-1 rounded-md hover:bg-[#F5F6F2] text-[#4B5158]"><X size={16}/></button>
            </div>
            <div className="p-5 space-y-3">
              <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">Motivo (opcional)</label>
              <textarea rows={3} className="ds-textarea" value={rejecting.comment}
                        onChange={(e) => setRejecting({ ...rejecting, comment: e.target.value })}
                        placeholder="Explica el motivo al empleado…" data-testid="admin-reject-comment"/>
              <div className="flex justify-end gap-2">
                <button className="ds-btn" onClick={() => setRejecting(null)}>Cancelar</button>
                <button className="ds-btn danger" onClick={doReject} data-testid="admin-reject-confirm">Rechazar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function groupConsecAdmin(datesISO) {
  const arr = [...datesISO].sort();
  const groups = [];
  for (const iso of arr) {
    const last = groups[groups.length - 1];
    if (last) {
      const [y,m,d] = last.end.split("-").map(Number);
      const next = new Date(y, m-1, d + 1);
      const nextISO = next.toISOString().slice(0, 10);
      if (nextISO === iso) { last.end = iso; last.count++; continue; }
    }
    groups.push({ start: iso, end: iso, count: 1 });
  }
  return groups;
}
function fmtAdmin(iso) {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}`;
}
function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

/* ============================================================ */
/* Empleados                                                     */
/* ============================================================ */

function EmployeesPanel() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [defaults, setDefaults] = useState({ days_per_year: 22, allow_negative_balance: false });
  const [defDraft, setDefDraft] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [editing, setEditing] = useState(null); // { user_id, days_annual }

  const load = async () => {
    const [d, list] = await Promise.all([
      api.get("/settings/employees/vacation-defaults"),
      api.get("/settings/employees", { params: { year } }),
    ]);
    setDefaults(d.data);
    setDefDraft(d.data);
    setEmployees(list.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const saveDefaults = async () => {
    try {
      const r = await api.put("/settings/employees/vacation-defaults", defDraft);
      setDefaults(r.data);
      toast.success("Configuración por defecto guardada");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveEmployeeConfig = async () => {
    if (!editing) return;
    try {
      await api.put(`/settings/employees/${editing.user_id}/vacation-config`, {
        year,
        days_annual: Number(editing.days_annual),
      });
      toast.success("Excepción individual guardada");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const removeException = async (uid) => {
    if (!window.confirm("¿Quitar la excepción individual? El empleado vuelve al valor por defecto.")) return;
    try {
      await api.delete(`/settings/employees/${uid}/vacation-config/${year}`);
      toast.success("Excepción eliminada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5">
      {/* Defaults */}
      <div className="ds-card p-5" data-testid="emp-defaults-card">
        <div className="font-display font-bold text-sm mb-3">Configuración por defecto</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">Días de vacaciones / año</label>
            <input
              type="number" min={0} max={60}
              className="ds-input mt-1"
              value={defDraft?.days_per_year ?? 22}
              onChange={(e) => setDefDraft({ ...defDraft, days_per_year: Number(e.target.value) })}
              data-testid="emp-default-days"
            />
          </div>
          <label className="text-sm flex items-center gap-2 pb-2">
            <input
              type="checkbox"
              checked={defDraft?.allow_negative_balance || false}
              onChange={(e) => setDefDraft({ ...defDraft, allow_negative_balance: e.target.checked })}
              data-testid="emp-default-allow-neg"
            />
            Permitir saldo negativo
          </label>
          <button className="ds-btn primary" onClick={saveDefaults} data-testid="emp-default-save">
            <Save size={13} /> Guardar
          </button>
        </div>
      </div>

      {/* Employees list */}
      <div className="ds-card">
        <div className="ds-card-h">
          <div className="t">Empleados · saldo del año</div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-[#8A9098]">Año</label>
            <select className="ds-select" style={{ padding: "5px 10px", fontSize: 12, width: 90 }}
                    value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="emp-year">
              {[year-1, year, year+1, year+2].map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono text-[10.5px] uppercase tracking-[.14em] text-[#8A9098] bg-[#FBFBF8] border-b border-[#E6E7E1]">
                <th className="px-4 py-2.5">Empleado</th>
                <th className="px-4 py-2.5">Email</th>
                <th className="px-4 py-2.5 text-right">Días anuales</th>
                <th className="px-4 py-2.5 text-right">Aprobados</th>
                <th className="px-4 py-2.5 text-right">Pendientes</th>
                <th className="px-4 py-2.5 text-right">Disponibles</th>
                <th className="px-4 py-2.5 w-40"></th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e) => {
                const isEditing = editing?.user_id === e.id;
                return (
                  <tr key={e.id} className="border-b border-[#E6E7E1] last:border-0" data-testid={`emp-row-${e.id}`}>
                    <td className="px-4 py-2.5 font-semibold">{e.name}</td>
                    <td className="px-4 py-2.5 text-[#4B5158] text-xs font-mono">{e.email}</td>
                    <td className="px-4 py-2.5 text-right font-mono">
                      {isEditing ? (
                        <input type="number" min={0} max={60}
                          className="ds-input text-right" style={{ width: 76, padding: "4px 8px", fontSize: 13 }}
                          value={editing.days_annual}
                          onChange={(ev) => setEditing({ ...editing, days_annual: ev.target.value })}
                          data-testid={`emp-days-input-${e.id}`}
                        />
                      ) : (
                        <span>{e.vacation.annual}{e.custom_days && <span className="ml-1 text-[9px] font-bold text-[#3F5222]">·</span>}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-[#4E2E86]">{e.vacation.approved}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-[#8C5A00]">{e.vacation.pending}</td>
                    <td className="px-4 py-2.5 text-right font-mono font-bold text-[#3F5222]">{e.vacation.available}</td>
                    <td className="px-4 py-2.5 text-right">
                      {isEditing ? (
                        <div className="inline-flex gap-1">
                          <button className="ds-btn primary" style={{ padding: "5px 10px", fontSize: 11 }} onClick={saveEmployeeConfig} data-testid={`emp-save-${e.id}`}>
                            <Save size={11} />
                          </button>
                          <button className="ds-btn" style={{ padding: "5px 10px", fontSize: 11 }} onClick={() => setEditing(null)} data-testid={`emp-cancel-${e.id}`}>
                            <X size={11} />
                          </button>
                        </div>
                      ) : (
                        <div className="inline-flex gap-1">
                          <button
                            className="ds-btn" style={{ padding: "5px 10px", fontSize: 11 }}
                            onClick={() => setEditing({ user_id: e.id, days_annual: e.vacation.annual })}
                            data-testid={`emp-edit-${e.id}`}
                          >
                            <Pencil size={11} /> Excepción
                          </button>
                          {e.custom_days && (
                            <button className="ds-btn danger" style={{ padding: "5px 10px", fontSize: 11 }} onClick={() => removeException(e.id)} title="Quitar excepción" data-testid={`emp-remove-${e.id}`}>
                              <Trash2 size={11} />
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {employees.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[#8A9098]">Sin empleados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ============================================================ */
/* Calendario laboral (Festivos)                                */
/* ============================================================ */

function HolidaysPanel() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ date: "", name: "", kind: "festivo" });
  const [editingId, setEditingId] = useState(null);
  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return [y-1, y, y+1, y+2];
  }, []);

  const load = async () => {
    const r = await api.get("/settings/holidays", { params: { year } });
    setItems(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.name.trim()) return toast.error("Fecha y nombre obligatorios");
    try {
      const payload = {
        year: Number(form.date.slice(0, 4)),
        date: form.date,
        name: form.name.trim(),
        kind: form.kind,
      };
      if (editingId) {
        await api.put(`/settings/holidays/${editingId}`, payload);
        toast.success("Festivo actualizado");
      } else {
        await api.post("/settings/holidays", payload);
        toast.success("Festivo creado");
      }
      setForm({ date: "", name: "", kind: "festivo" });
      setEditingId(null);
      // Recargar según el año del festivo creado.
      if (payload.year !== year) setYear(payload.year);
      else load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const startEdit = (h) => {
    setEditingId(h.id);
    setForm({ date: h.date, name: h.name, kind: h.kind });
  };
  const cancelEdit = () => { setEditingId(null); setForm({ date: "", name: "", kind: "festivo" }); };
  const remove = async (h) => {
    if (!window.confirm(`¿Eliminar "${h.name}" (${h.date})?`)) return;
    try { await api.delete(`/settings/holidays/${h.id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5">
      <form onSubmit={submit} className="ds-card p-5" data-testid="holiday-form">
        <div className="font-display font-bold text-sm mb-3">
          {editingId ? "Editar día no laborable" : "Añadir día no laborable"}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[160px_1fr_180px_auto_auto] gap-3 items-end">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">Fecha</label>
            <input type="date" className="ds-input mt-1"
                   value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required
                   data-testid="holiday-date"/>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">Nombre / descripción</label>
            <input className="ds-input mt-1" placeholder="Ej. Año Nuevo, Reyes, Festivo local…"
                   value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                   data-testid="holiday-name"/>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[.14em] text-[#8A9098] font-semibold">Tipo</label>
            <select className="ds-select mt-1" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="holiday-kind">
              <option value="festivo">Festivo</option>
              <option value="no_laborable">No laborable</option>
            </select>
          </div>
          <button type="submit" className="ds-btn primary" data-testid="holiday-submit">
            <Plus size={13} /> {editingId ? "Guardar" : "Añadir"}
          </button>
          {editingId && (
            <button type="button" className="ds-btn" onClick={cancelEdit}>
              <X size={13} /> Cancelar
            </button>
          )}
        </div>
      </form>

      <div className="ds-card">
        <div className="ds-card-h">
          <div className="t">Días no laborables · {year}</div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-[#8A9098]">Año</label>
            <select className="ds-select" style={{ padding: "5px 10px", fontSize: 12, width: 90 }}
                    value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="holiday-year">
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left font-mono text-[10.5px] uppercase tracking-[.14em] text-[#8A9098] bg-[#FBFBF8] border-b border-[#E6E7E1]">
              <th className="px-4 py-2.5">Fecha</th>
              <th className="px-4 py-2.5">Nombre</th>
              <th className="px-4 py-2.5">Tipo</th>
              <th className="px-4 py-2.5 w-28 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((h) => (
              <tr key={h.id} className="border-b border-[#E6E7E1] last:border-0" data-testid={`holiday-row-${h.id}`}>
                <td className="px-4 py-2.5 font-mono text-xs">{h.date}</td>
                <td className="px-4 py-2.5 font-semibold">{h.name}</td>
                <td className="px-4 py-2.5">
                  <span className={`ds-pill ${h.kind === "festivo" ? "" : ""}`}
                        style={h.kind === "festivo"
                          ? { background: "#FDEDE7", color: "#8C2B14", borderColor: "rgba(180,56,31,.22)" }
                          : { background: "#E7EEFD", color: "#1D4EBF", borderColor: "rgba(47,111,237,.18)" }}>
                    <span className="dot" style={{ background: h.kind === "festivo" ? "#B4381F" : "#2F6FED" }} />
                    {h.kind === "festivo" ? "Festivo" : "No laborable"}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="inline-flex gap-1">
                    <button className="ds-btn" style={{ padding: "5px 10px", fontSize: 11 }} onClick={() => startEdit(h)} data-testid={`holiday-edit-${h.id}`}>
                      <Pencil size={11} />
                    </button>
                    <button className="ds-btn danger" style={{ padding: "5px 10px", fontSize: 11 }} onClick={() => remove(h)} data-testid={`holiday-delete-${h.id}`}>
                      <Trash2 size={11} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-[#8A9098]">Sin días no laborables para {year}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
